import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '@frontend/contexts/AuthContext';
import { TaskDetailDialog, useTaskStoreConnection } from '@modules/background-tasks';
import { SDialog, StyledTextField } from '@modules/ui-components';
import { useBillingCatalog } from '../hooks/useBillingCatalog';
import CreditLedgerHistoryDialog from '../components/CreditLedgerHistoryDialog';
import type {
  BillingCreditLedgerEntry,
  BillingModule,
  BillingProduct,
  BillingProductKind,
  BillingProductStatus,
  BillingFundingSource,
  BillingSubscriptionStatus,
  BillingSubscription,
  BillingProjectSummary,
  BillingUsageMeter,
  BillingUsageMeterKind,
} from '../types';

const BASE_MODULE_OPTIONS: Array<{ value: BillingModule; label: string }> = [
  { value: 'product_analysis', label: 'Product' },
  { value: 'content_plan', label: 'Content Plan' },
  { value: 'web_onboarding', label: 'Web Onboarding' },
  { value: 'funnel_insights', label: 'Funnel Insights' },
  { value: 'ios_sdk', label: 'iOS SDK' },
  { value: 'articles', label: 'Articles' },
  { value: 'ai', label: 'AI' },
  { value: 'credits', label: 'Credits' },
  { value: 'platform', label: 'Platform' },
];

const STATUS_OPTIONS: BillingProductStatus[] = ['draft', 'active', 'archived'];
const PRODUCT_KIND_OPTIONS: Array<{ value: BillingProductKind; label: string }> = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'addon', label: 'Addon' },
  { value: 'credit_pack', label: 'Credit pack' },
];
const METER_KIND_OPTIONS: BillingUsageMeterKind[] = [
  'static',
  'web_onboarding_usage',
  'ios_sdk_usage',
  'ai_tokens',
];
const SUBSCRIPTION_STATUS_OPTIONS: BillingSubscriptionStatus[] = [
  'trialing',
  'active',
  'past_due',
  'paused',
  'canceled',
  'expired',
];
const FUNDING_SOURCE_OPTIONS: BillingFundingSource[] = ['manual_grant', 'stripe', 'credits'];
const BILLING_TABS = [
  { value: 'projects', label: 'Projects' },
  { value: 'meters', label: 'Usage meters' },
  { value: 'types', label: 'Subscription types' },
] as const;
type BillingTab = typeof BILLING_TABS[number]['value'];

interface ProductDraft {
  id: string;
  kind: BillingProductKind;
  name: string;
  description: string;
  module: BillingModule;
  status: BillingProductStatus;
  capabilities: string;
  priceUsd: string;
  stripePriceId: string;
  creditsIncludedMinor: string;
  trialDays: string;
  includedMeterId: string;
  includedQuantity: string;
}

const emptyDraft: ProductDraft = {
  id: '',
  kind: 'subscription',
  name: '',
  description: '',
  module: 'content_plan',
  status: 'draft',
  capabilities: 'content_plan.*',
  priceUsd: '',
  stripePriceId: '',
  creditsIncludedMinor: '',
  trialDays: '0',
  includedMeterId: '',
  includedQuantity: '',
};

interface SubscriptionDraft {
  productId: string;
  status: BillingSubscriptionStatus;
  fundingSource: BillingFundingSource;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt: string;
}

interface CreditGrantDraft {
  amountUsd: string;
  source: string;
  sourceId: string;
}

interface CreditDebitDraft {
  amountUsd: string;
  source: string;
  sourceId: string;
}

interface MeterDraft {
  id: string;
  name: string;
  kind: BillingUsageMeterKind;
  unit: string;
  unitPriceUsd: string;
  aiModelKey: string;
  aiInputUsdPer1k: string;
  aiOutputUsdPer1k: string;
  allowCreditOverage: boolean;
}

const emptyMeterDraft: MeterDraft = {
  id: '',
  name: '',
  kind: 'ai_tokens',
  unit: 'token',
  unitPriceUsd: '',
  aiModelKey: 'openai:gpt-5.6-terra',
  aiInputUsdPer1k: '',
  aiOutputUsdPer1k: '',
  allowCreditOverage: true,
};

function parseLines(value: string): string[] {
  return value
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function moneyToLabel(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

function parsePositiveMinorAmount(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parseUsdToMinor(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  const amountMinor = Math.round(parsed * 100);
  return Number.isSafeInteger(amountMinor) && amountMinor > 0 ? amountMinor : undefined;
}

function buildProductPayload(draft: ProductDraft): Partial<BillingProduct> {
  const priceMinor = draft.priceUsd.trim()
    ? Math.round(Number(draft.priceUsd) * 100)
    : 0;
  const creditsIncludedMinor = draft.creditsIncludedMinor.trim()
    ? Math.max(0, Math.floor(Number(draft.creditsIncludedMinor)))
    : 0;
  const trialDays = Math.max(0, Math.floor(Number(draft.trialDays || 0)));
  return {
    id: draft.id.trim() || undefined,
    kind: draft.kind,
    status: draft.status,
    name: draft.name.trim(),
    description: draft.description.trim() || undefined,
    module: draft.module,
    capabilities: parseLines(draft.capabilities),
    prices: priceMinor > 0
      ? [{
          provider: draft.stripePriceId.trim() ? 'stripe' : 'manual_grant',
          amountMinor: priceMinor,
          currency: 'usd',
          interval: draft.kind === 'credit_pack' ? 'one_time' : 'month',
          stripePriceId: draft.stripePriceId.trim() || undefined,
          creditsIncludedMinor: draft.kind === 'credit_pack' ? creditsIncludedMinor : undefined,
        }]
      : [],
    includedUsage: draft.kind !== 'credit_pack' && draft.includedMeterId.trim()
      ? [{
          meterId: draft.includedMeterId.trim(),
          quantity: Math.max(0, Math.floor(Number(draft.includedQuantity || 0))),
          resetInterval: 'subscription_period',
        }]
      : [],
    trial: draft.kind !== 'credit_pack' && trialDays > 0
      ? {
          type: 'free',
          days: trialDays,
        }
      : { type: 'none' },
    metadata: draft.kind === 'credit_pack' && creditsIncludedMinor > 0
      ? { creditsIncludedMinor }
      : undefined,
  };
}

function productToDraft(product?: BillingProduct | null): ProductDraft {
  if (!product) return emptyDraft;
  const primaryPrice = product.prices?.[0];
  const includedUsage = product.includedUsage?.[0];
  return {
    id: product.id,
    kind: product.kind,
    name: product.name,
    description: product.description || '',
    module: product.module || 'platform',
    status: product.status,
    capabilities: (product.capabilities || []).join('\n'),
    priceUsd: primaryPrice ? String(primaryPrice.amountMinor / 100) : '',
    stripePriceId: primaryPrice?.stripePriceId || '',
    creditsIncludedMinor: primaryPrice?.creditsIncludedMinor
      ? String(primaryPrice.creditsIncludedMinor)
      : String(product.metadata?.creditsIncludedMinor || ''),
    trialDays: product.trial?.type === 'free' ? String(product.trial.days || 0) : '0',
    includedMeterId: includedUsage?.meterId || '',
    includedQuantity: includedUsage ? String(includedUsage.quantity) : '',
  };
}

function subscriptionToDraft(subscription: BillingSubscription): SubscriptionDraft {
  return {
    productId: subscription.productId,
    status: subscription.status,
    fundingSource: subscription.fundingSource,
    currentPeriodStart: toDateTimeLocalInput(subscription.currentPeriodStart),
    currentPeriodEnd: toDateTimeLocalInput(subscription.currentPeriodEnd),
    trialEndsAt: toDateTimeLocalInput(subscription.trialEndsAt),
  };
}

function toDateTimeLocalInput(value?: string | Date): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function defaultSubscriptionDraft(): SubscriptionDraft {
  const now = new Date();
  return {
    productId: '',
    status: 'active',
    fundingSource: 'manual_grant',
    currentPeriodStart: toDateTimeLocalInput(now),
    currentPeriodEnd: toDateTimeLocalInput(addMonths(now, 1)),
    trialEndsAt: '',
  };
}

function generateCreditSourceId(projectId: string | undefined, action: 'credit' | 'debit'): string {
  const safeProjectId = (projectId || 'project')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .slice(0, 28) || 'project';
  const suffix = Math.random().toString(36).slice(2, 8);
  return `manual_${action}_${safeProjectId}_${Date.now()}_${suffix}`;
}

function defaultCreditGrantDraft(projectId?: string): CreditGrantDraft {
  return {
    amountUsd: '',
    source: 'manual_admin_grant',
    sourceId: generateCreditSourceId(projectId, 'credit'),
  };
}

function defaultCreditDebitDraft(projectId?: string): CreditDebitDraft {
  return {
    amountUsd: '',
    source: 'manual_admin_debit',
    sourceId: generateCreditSourceId(projectId, 'debit'),
  };
}

function dateTimeLocalToIso(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function buildSubscriptionPayload(draft: SubscriptionDraft) {
  return {
    productId: draft.productId,
    status: draft.status,
    fundingSource: draft.fundingSource,
    currentPeriodStart: dateTimeLocalToIso(draft.currentPeriodStart),
    currentPeriodEnd: draft.currentPeriodEnd.trim() ? dateTimeLocalToIso(draft.currentPeriodEnd) : null,
    trialEndsAt: draft.trialEndsAt.trim() ? dateTimeLocalToIso(draft.trialEndsAt) : null,
  };
}

function projectSearchHelperText(query: string, loading: boolean, resultCount: number): string {
  const base = 'Search by project name, project ID, owner ID, or member email';
  if (loading) {
    return `${base} · searching...`;
  }
  if (query.trim() && resultCount === 0) {
    return `${base} · no projects found`;
  }
  if (resultCount > 0) {
    return `${base} · ${resultCount} result${resultCount === 1 ? '' : 's'}`;
  }
  return base;
}

function buildMeterPayload(draft: MeterDraft): Partial<BillingUsageMeter> {
  const unitAmountMinor = draft.unitPriceUsd.trim()
    ? Math.round(Number(draft.unitPriceUsd) * 100)
    : undefined;
  const aiInputMinor = draft.aiInputUsdPer1k.trim()
    ? Math.round(Number(draft.aiInputUsdPer1k) * 100)
    : undefined;
  const aiOutputMinor = draft.aiOutputUsdPer1k.trim()
    ? Math.round(Number(draft.aiOutputUsdPer1k) * 100)
    : undefined;
  return {
    id: draft.id.trim() || undefined,
    kind: draft.kind,
    status: 'active',
    name: draft.name.trim(),
    unit: draft.unit.trim() || 'unit',
    currency: 'usd',
    aggregation: 'sum',
    unitAmountMinor,
    allowCreditOverage: draft.allowCreditOverage,
    aiModelPrices: draft.kind === 'ai_tokens' && draft.aiModelKey.trim()
      ? {
          [draft.aiModelKey.trim()]: {
            inputTokenAmountMinorPer1k: aiInputMinor || 0,
            outputTokenAmountMinorPer1k: aiOutputMinor || 0,
            currency: 'usd',
          },
        }
      : undefined,
  };
}

function ledgerDeltaLabel(entry: BillingCreditLedgerEntry): string {
  const sign = entry.deltaMinor >= 0 ? '+' : '-';
  return `${sign}${moneyToLabel(Math.abs(entry.deltaMinor), entry.currency)}`;
}

function ProductRow({
  product,
  onEdit,
  onDelete,
  saving,
}: {
  product: BillingProduct;
  onEdit: (product: BillingProduct) => void;
  onDelete: (productId: string) => Promise<void>;
  saving: boolean;
}): React.ReactElement {
  const primaryPrice = product.prices?.[0];
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1">{product.name}</Typography>
            <Chip size="small" label={product.status} variant={product.status === 'active' ? 'filled' : 'outlined'} />
            <Chip size="small" label={product.kind} variant="outlined" />
            <Chip size="small" label={product.module} variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {product.description || product.id}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
            {(product.capabilities || []).map((capability) => (
              <Chip key={capability} size="small" label={capability} />
            ))}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          {primaryPrice && (
            <Typography variant="body2" color="text.secondary">
              {moneyToLabel(primaryPrice.amountMinor, primaryPrice.currency)} / {primaryPrice.interval || 'period'}
              {primaryPrice.creditsIncludedMinor ? ` · ${moneyToLabel(primaryPrice.creditsIncludedMinor, primaryPrice.currency)} credits` : ''}
            </Typography>
          )}
          <Button
            size="small"
            startIcon={<EditIcon />}
            disabled={saving}
            onClick={() => onEdit(product)}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<ArchiveIcon />}
            disabled={saving || product.status === 'archived'}
            onClick={() => onDelete(product.id)}
          >
            Delete type
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function SubscriptionRow({
  subscription,
  onView,
  onEdit,
  onDelete,
  saving,
}: {
  subscription: BillingSubscription;
  onView: (subscription: BillingSubscription) => void;
  onEdit: (subscription: BillingSubscription) => void;
  onDelete: (subscription: BillingSubscription) => Promise<void>;
  saving: boolean;
}): React.ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">{subscription.productName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {subscription.id} · {subscription.productId}
          </Typography>
        </Box>
        <Chip size="small" label={subscription.status} />
        <Chip size="small" label={subscription.fundingSource} variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {new Date(subscription.currentPeriodStart).toLocaleDateString()}
          {' - '}
          {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'no end'}
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" startIcon={<VisibilityIcon />} onClick={() => onView(subscription)}>
            View
          </Button>
          <Button size="small" startIcon={<EditIcon />} disabled={saving} onClick={() => onEdit(subscription)}>
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={saving}
            onClick={() => onDelete(subscription)}
          >
            Delete
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ProjectRow({
  project,
  onOpen,
}: {
  project: BillingProjectSummary;
  onOpen: (project: BillingProjectSummary) => void;
}): React.ReactElement {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        },
      }}
      onClick={() => onOpen(project)}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">{project.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {project.id}
            {project.aliases?.length ? ` · also ${project.aliases.slice(0, 2).join(', ')}` : ''}
            {project.ownerId ? ` · owner ${project.ownerId}` : ''}
            {project.memberEmails?.length ? ` · ${project.memberEmails.slice(0, 3).join(', ')}` : ''}
          </Typography>
          {project.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {project.description}
            </Typography>
          )}
        </Box>
        <Button size="small" startIcon={<VisibilityIcon />} onClick={(event) => {
          event.stopPropagation();
          onOpen(project);
        }}>
          Open
        </Button>
      </Stack>
    </Paper>
  );
}

export default function BillingCatalogAdminPage(): React.ReactElement {
  const { isAdmin, adminLoaded, currentUser } = useAuth();
  const {
    products,
    meters,
    projects,
    projectSubscriptions,
    projectAccount,
    projectCreditLedger,
    loading,
    saving,
    projectsLoading,
    subscriptionsLoading,
    projectCreditsLoading,
    error,
    refresh,
    searchProjects,
    createProduct,
    updateProduct,
    archiveProduct,
    createMeter,
    loadProjectSubscriptions,
    createManualSubscription,
    updateProjectSubscription,
    deleteProjectSubscription,
    loadProjectCreditState,
    grantProjectCredits,
    debitProjectCredits,
  } = useBillingCatalog(adminLoaded && isAdmin);
  const [draft, setDraft] = useState<ProductDraft>(emptyDraft);
  const [meterDraft, setMeterDraft] = useState<MeterDraft>(emptyMeterDraft);
  const [activeTab, setActiveTab] = useState<BillingTab>('projects');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [meterDialogOpen, setMeterDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [manualSubscriptionDialogOpen, setManualSubscriptionDialogOpen] = useState(false);
  const [creditGrantDialogOpen, setCreditGrantDialogOpen] = useState(false);
  const [creditDebitDialogOpen, setCreditDebitDialogOpen] = useState(false);
  const [creditHistoryDialogOpen, setCreditHistoryDialogOpen] = useState(false);
  const [selectedLedgerTaskId, setSelectedLedgerTaskId] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<BillingProduct | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProject, setSelectedProject] = useState<BillingProjectSummary | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [manualSubscriptionDraft, setManualSubscriptionDraft] = useState<SubscriptionDraft>(() => defaultSubscriptionDraft());
  const [creditGrantDraft, setCreditGrantDraft] = useState<CreditGrantDraft>(() => defaultCreditGrantDraft());
  const [creditDebitDraft, setCreditDebitDraft] = useState<CreditDebitDraft>(() => defaultCreditDebitDraft());
  const [subscriptionDialogMode, setSubscriptionDialogMode] = useState<'view' | 'edit' | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<BillingSubscription | null>(null);
  const [subscriptionDraft, setSubscriptionDraft] = useState<SubscriptionDraft | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const catalogProducts = useMemo(
    () => products.filter((product) => product.kind !== 'usage_meter' && product.status !== 'archived'),
    [products],
  );
  const subscriptionProducts = useMemo(
    () => products.filter((product) => (product.kind === 'subscription' || product.kind === 'addon') && product.status !== 'archived'),
    [products],
  );
  const activeSubscriptionProducts = useMemo(
    () => subscriptionProducts.filter((product) => product.status === 'active'),
    [subscriptionProducts],
  );

  useTaskStoreConnection(
    projectDialogOpen && selectedProjectId ? selectedProjectId : null,
    currentUser?.uid,
  );

  useEffect(() => {
    if (selectedProjectId) {
      loadProjectSubscriptions(selectedProjectId);
      loadProjectCreditState(selectedProjectId);
    }
  }, [loadProjectCreditState, loadProjectSubscriptions, selectedProjectId]);

  useEffect(() => {
    if (!adminLoaded || !isAdmin) return undefined;
    const timeout = window.setTimeout(() => {
      searchProjects(projectSearch);
    }, projectSearch.trim() ? 300 : 0);
    return () => window.clearTimeout(timeout);
  }, [adminLoaded, isAdmin, projectSearch, searchProjects]);

  if (!adminLoaded) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Checking admin access...</Alert>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  const updateDraft = (key: keyof ProductDraft, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const updateMeterDraft = (key: keyof MeterDraft, value: string | boolean) => {
    setMeterDraft((current) => ({ ...current, [key]: value }));
  };

  const openCreateProductDialog = () => {
    setEditingProduct(null);
    setDraft(emptyDraft);
    setActiveTab('types');
    setProductDialogOpen(true);
  };

  const openEditProductDialog = (product: BillingProduct) => {
    setEditingProduct(product);
    setDraft(productToDraft(product));
    setActiveTab('types');
    setProductDialogOpen(true);
  };

  const closeProductDialog = () => {
    setProductDialogOpen(false);
    setEditingProduct(null);
    setDraft(emptyDraft);
  };

  const openCreateMeterDialog = () => {
    setActiveTab('meters');
    setMeterDraft(emptyMeterDraft);
    setMeterDialogOpen(true);
  };

  const closeMeterDialog = () => {
    setMeterDialogOpen(false);
    setMeterDraft(emptyMeterDraft);
  };

  const openProjectDialog = (project: BillingProjectSummary) => {
    setSelectedProject(project);
    setSelectedProjectId(project.id);
    setManualSubscriptionDraft(defaultSubscriptionDraft());
    setProjectDialogOpen(true);
  };

  const closeProjectDialog = () => {
    setProjectDialogOpen(false);
    setManualSubscriptionDialogOpen(false);
    setCreditGrantDialogOpen(false);
    setCreditDebitDialogOpen(false);
    setCreditHistoryDialogOpen(false);
    setSelectedLedgerTaskId(null);
    setManualSubscriptionDraft(defaultSubscriptionDraft());
    setCreditGrantDraft(defaultCreditGrantDraft());
    setCreditDebitDraft(defaultCreditDebitDraft());
    setSelectedProject(null);
    setSelectedProjectId('');
    setSelectedSubscription(null);
    setSubscriptionDraft(null);
    setSubscriptionDialogMode(null);
  };

  const handleSaveProduct = async () => {
    const payload = buildProductPayload(draft);
    if (!payload.name) {
      setSuccess(null);
      return;
    }
    const product = editingProduct
      ? await updateProduct(editingProduct.id, payload)
      : await createProduct(payload);
    closeProductDialog();
    setSuccess(`Subscription type "${product.name}" ${editingProduct ? 'updated' : 'created'}`);
  };

  const handleDeleteProductType = async (productId: string) => {
    const confirmed = window.confirm(`Delete subscription type ${productId}? Existing project subscriptions will remain unchanged.`);
    if (!confirmed) return;
    await archiveProduct(productId);
    setSuccess(`Subscription type ${productId} deleted`);
  };

  const handleCreateMeter = async () => {
    const payload = buildMeterPayload(meterDraft);
    if (!payload.name) {
      setSuccess(null);
      return;
    }
    const meter = await createMeter(payload);
    closeMeterDialog();
    setSuccess(`Usage meter "${meter.name}" created`);
  };

  const openCreateManualSubscriptionDialog = () => {
    setManualSubscriptionDraft(defaultSubscriptionDraft());
    setManualSubscriptionDialogOpen(true);
  };

  const closeCreateManualSubscriptionDialog = () => {
    setManualSubscriptionDialogOpen(false);
    setManualSubscriptionDraft(defaultSubscriptionDraft());
  };

  const updateManualSubscriptionDraft = (key: keyof SubscriptionDraft, value: string) => {
    setManualSubscriptionDraft((current) => ({ ...current, [key]: value }));
  };

  const handleManualSubscription = async () => {
    if (!selectedProjectId || !manualSubscriptionDraft.productId) return;
    const subscription = await createManualSubscription(selectedProjectId, buildSubscriptionPayload(manualSubscriptionDraft));
    closeCreateManualSubscriptionDialog();
    setSuccess(`Manual subscription "${subscription.productName}" added to ${selectedProject?.name || selectedProjectId}`);
  };

  const openCreditGrantDialog = () => {
    setCreditGrantDraft(defaultCreditGrantDraft(selectedProjectId));
    setCreditGrantDialogOpen(true);
  };

  const closeCreditGrantDialog = () => {
    setCreditGrantDialogOpen(false);
    setCreditGrantDraft(defaultCreditGrantDraft());
  };

  const updateCreditGrantDraft = (key: keyof CreditGrantDraft, value: string) => {
    setCreditGrantDraft((current) => ({ ...current, [key]: value }));
  };

  const handleCreditGrant = async () => {
    if (!selectedProjectId) return;
    const amountMinor = parseUsdToMinor(creditGrantDraft.amountUsd);
    if (!amountMinor) return;
    const entry = await grantProjectCredits(selectedProjectId, {
      amountMinor,
      source: creditGrantDraft.source.trim() || 'manual_admin_grant',
      sourceId: creditGrantDraft.sourceId.trim() || undefined,
    });
    closeCreditGrantDialog();
    setSuccess(
      `Added ${moneyToLabel(Math.abs(entry.deltaMinor), entry.currency)} credits to ${selectedProject?.name || selectedProjectId}`,
    );
  };

  const openCreditDebitDialog = () => {
    setCreditDebitDraft(defaultCreditDebitDraft(selectedProjectId));
    setCreditDebitDialogOpen(true);
  };

  const closeCreditDebitDialog = () => {
    setCreditDebitDialogOpen(false);
    setCreditDebitDraft(defaultCreditDebitDraft());
  };

  const updateCreditDebitDraft = (key: keyof CreditDebitDraft, value: string) => {
    setCreditDebitDraft((current) => ({ ...current, [key]: value }));
  };

  const handleCreditDebit = async () => {
    if (!selectedProjectId) return;
    const amountMinor = parseUsdToMinor(creditDebitDraft.amountUsd);
    if (!amountMinor) return;
    const entry = await debitProjectCredits(selectedProjectId, {
      amountMinor,
      source: creditDebitDraft.source.trim() || 'manual_admin_debit',
      sourceId: creditDebitDraft.sourceId.trim() || undefined,
    });
    closeCreditDebitDialog();
    setSuccess(
      `Reduced ${moneyToLabel(Math.abs(entry.deltaMinor), entry.currency)} credits from ${selectedProject?.name || selectedProjectId}`,
    );
  };

  const openViewSubscriptionDialog = (subscription: BillingSubscription) => {
    setSelectedSubscription(subscription);
    setSubscriptionDraft(subscriptionToDraft(subscription));
    setSubscriptionDialogMode('view');
  };

  const openEditSubscriptionDialog = (subscription: BillingSubscription) => {
    setSelectedSubscription(subscription);
    setSubscriptionDraft(subscriptionToDraft(subscription));
    setSubscriptionDialogMode('edit');
  };

  const closeSubscriptionDialog = () => {
    setSelectedSubscription(null);
    setSubscriptionDraft(null);
    setSubscriptionDialogMode(null);
  };

  const updateSubscriptionDraft = (key: keyof SubscriptionDraft, value: string) => {
    setSubscriptionDraft((current) => current ? ({ ...current, [key]: value }) : current);
  };

  const handleSaveProjectSubscription = async () => {
    if (!selectedProjectId || !selectedSubscription || !subscriptionDraft) return;
    const updated = await updateProjectSubscription(
      selectedProjectId,
      selectedSubscription.id,
      buildSubscriptionPayload(subscriptionDraft),
    );
    closeSubscriptionDialog();
    setSuccess(`Project subscription "${updated.productName}" updated`);
  };

  const handleDeleteProjectSubscription = async (subscription: BillingSubscription) => {
    if (!selectedProjectId) return;
    const confirmed = window.confirm(`Delete subscription "${subscription.productName}" from ${selectedProject?.name || selectedProjectId}?`);
    if (!confirmed) return;
    await deleteProjectSubscription(selectedProjectId, subscription.id);
    setSuccess(`Project subscription "${subscription.productName}" deleted`);
  };

  const handleRefresh = async () => {
    await Promise.all([
      refresh(),
      searchProjects(projectSearch),
    ]);
    if (selectedProjectId) {
      await Promise.all([
        loadProjectSubscriptions(selectedProjectId),
        loadProjectCreditState(selectedProjectId),
      ]);
    }
  };

  const selectedCreditCurrency = projectAccount?.creditBalance.currency || 'usd';
  const selectedAvailableCredits = projectAccount?.creditBalance.availableMinor || 0;
  const selectedReservedCredits = projectAccount?.creditBalance.reservedMinor || 0;
  const selectedSpendableCredits = selectedAvailableCredits - selectedReservedCredits;
  const creditDebitAmountMinor = parseUsdToMinor(creditDebitDraft.amountUsd);
  const recentProjectCreditLedger = projectCreditLedger.slice(0, 3);

  const renderSubscriptionDraftFields = (
    currentDraft: SubscriptionDraft,
    onDraftChange: (key: keyof SubscriptionDraft, value: string) => void,
  ) => (
    <Stack spacing={1.5}>
      <FormControl size="small" fullWidth>
        <InputLabel id="project-subscription-product-label">Subscription type</InputLabel>
        <Select
          labelId="project-subscription-product-label"
          label="Subscription type"
          value={currentDraft.productId}
          onChange={(event) => onDraftChange('productId', event.target.value)}
        >
          <MenuItem value="">Select subscription type</MenuItem>
          {activeSubscriptionProducts.map((product) => (
            <MenuItem key={product.id} value={product.id}>
              {product.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <FormControl size="small" fullWidth>
          <InputLabel id="project-subscription-status-label">Status</InputLabel>
          <Select
            labelId="project-subscription-status-label"
            label="Status"
            value={currentDraft.status}
            onChange={(event) => onDraftChange('status', event.target.value)}
          >
            {SUBSCRIPTION_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id="project-subscription-funding-label">Funding source</InputLabel>
          <Select
            labelId="project-subscription-funding-label"
            label="Funding source"
            value={currentDraft.fundingSource}
            onChange={(event) => onDraftChange('fundingSource', event.target.value)}
          >
            {FUNDING_SOURCE_OPTIONS.map((source) => (
              <MenuItem key={source} value={source}>{source}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          label="Starts at"
          type="datetime-local"
          size="small"
          fullWidth
          required
          value={currentDraft.currentPeriodStart}
          onChange={(event) => onDraftChange('currentPeriodStart', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Ends at"
          type="datetime-local"
          size="small"
          fullWidth
          value={currentDraft.currentPeriodEnd}
          onChange={(event) => onDraftChange('currentPeriodEnd', event.target.value)}
          helperText="Leave empty for an indefinite manual grant"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Trial ends at"
          type="datetime-local"
          size="small"
          fullWidth
          value={currentDraft.trialEndsAt}
          onChange={(event) => onDraftChange('trialEndsAt', event.target.value)}
          helperText="Only used while status is trialing"
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1220 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">Billing catalog</Typography>
          <Typography variant="body2" color="text.secondary">
            Admin-managed platform subscription types, capabilities, trials, prices, and usage quotas.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading || saving || projectsLoading || subscriptionsLoading}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateMeterDialog} disabled={saving}>
            Add usage meter
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateProductDialog} disabled={saving}>
            Add subscription type
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <SDialog
        open={productDialogOpen}
        onClose={closeProductDialog}
        title={editingProduct ? 'Edit subscription type' : 'Add subscription type'}
        maxWidth="md"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeProductDialog,
          disabled: saving,
        }}
        primaryAction={{
          label: editingProduct ? 'Save subscription type' : 'Create subscription type',
          onClick: handleSaveProduct,
          loading: saving,
          disabled: !draft.name.trim()
            || (draft.kind !== 'credit_pack' && parseLines(draft.capabilities).length === 0)
            || (draft.kind === 'credit_pack' && !parsePositiveMinorAmount(draft.creditsIncludedMinor)),
        }}
      >
        <Stack spacing={1.5} sx={{ px: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <StyledTextField
              label="Name"
              value={draft.name}
              onChange={(value) => updateDraft('name', value)}
              size="small"
              fullWidth
              required
            />
            <StyledTextField
              label="Optional ID"
              value={draft.id}
              onChange={(value) => updateDraft('id', value)}
              size="small"
              fullWidth
              disabled={Boolean(editingProduct)}
              helperText={editingProduct ? 'ID cannot be changed after creation' : 'Leave empty to generate from name'}
            />
          </Stack>
          <StyledTextField
            label="Description"
            value={draft.description}
            onChange={(value) => updateDraft('description', value)}
            size="small"
            fullWidth
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="billing-product-kind-label">Product kind</InputLabel>
              <Select
                labelId="billing-product-kind-label"
                label="Product kind"
                value={draft.kind}
                onChange={(event) => updateDraft('kind', event.target.value)}
              >
                {PRODUCT_KIND_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={BASE_MODULE_OPTIONS.map((option) => option.value)}
              value={draft.module}
              inputValue={draft.module}
              onInputChange={(_event, value) => updateDraft('module', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Subscription type key"
                  helperText="Choose a base key or enter a new one"
                />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="billing-status-label">Status</InputLabel>
              <Select
                labelId="billing-status-label"
                label="Status"
                value={draft.status}
                onChange={(event) => updateDraft('status', event.target.value)}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {BASE_MODULE_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                size="small"
                label={option.label}
                variant={draft.module === option.value ? 'filled' : 'outlined'}
                onClick={() => updateDraft('module', option.value)}
              />
            ))}
          </Stack>
          <StyledTextField
            label="Capabilities"
            value={draft.capabilities}
            onChange={(value) => updateDraft('capabilities', value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            helperText="One per line or comma-separated, for example: content_plan.*, ai.*"
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <StyledTextField
              label="Monthly price USD"
              value={draft.priceUsd}
              onChange={(value) => updateDraft('priceUsd', value)}
              size="small"
              fullWidth
            />
            <StyledTextField
              label="Stripe price ID"
              value={draft.stripePriceId}
              onChange={(value) => updateDraft('stripePriceId', value)}
              size="small"
              fullWidth
              helperText="Optional for manual/internal grants"
            />
            {draft.kind === 'credit_pack' && (
              <StyledTextField
                label="Credits included minor"
                value={draft.creditsIncludedMinor}
                onChange={(value) => updateDraft('creditsIncludedMinor', value)}
                size="small"
                fullWidth
                helperText="Segmently credit amount added to the project balance"
              />
            )}
            <StyledTextField
              label="Free trial days"
              value={draft.trialDays}
              onChange={(value) => updateDraft('trialDays', value)}
              size="small"
              fullWidth
              disabled={draft.kind === 'credit_pack'}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="billing-meter-label">Included usage meter</InputLabel>
              <Select
                labelId="billing-meter-label"
                label="Included usage meter"
                value={draft.includedMeterId}
                onChange={(event) => updateDraft('includedMeterId', event.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {meters.map((meter) => (
                  <MenuItem key={meter.id} value={meter.id}>{meter.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <StyledTextField
              label="Included quantity"
              value={draft.includedQuantity}
              onChange={(value) => updateDraft('includedQuantity', value)}
              size="small"
              fullWidth
            />
          </Stack>
        </Stack>
      </SDialog>

      <SDialog
        open={meterDialogOpen}
        onClose={closeMeterDialog}
        title="Add usage meter"
        maxWidth="md"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeMeterDialog,
          disabled: saving,
        }}
        primaryAction={{
          label: 'Create usage meter',
          onClick: handleCreateMeter,
          loading: saving,
          disabled: !meterDraft.name.trim(),
        }}
      >
        <Stack spacing={1.5} sx={{ px: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <StyledTextField
              label="Meter name"
              value={meterDraft.name}
              onChange={(value) => updateMeterDraft('name', value)}
              size="small"
              fullWidth
              required
            />
            <StyledTextField
              label="Optional meter ID"
              value={meterDraft.id}
              onChange={(value) => updateMeterDraft('id', value)}
              size="small"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="billing-meter-kind-label">Kind</InputLabel>
              <Select
                labelId="billing-meter-kind-label"
                label="Kind"
                value={meterDraft.kind}
                onChange={(event) => updateMeterDraft('kind', event.target.value)}
              >
                {METER_KIND_OPTIONS.map((kind) => (
                  <MenuItem key={kind} value={kind}>{kind}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <StyledTextField
              label="Unit"
              value={meterDraft.unit}
              onChange={(value) => updateMeterDraft('unit', value)}
              size="small"
              fullWidth
            />
            <StyledTextField
              label="Unit price USD"
              value={meterDraft.unitPriceUsd}
              onChange={(value) => updateMeterDraft('unitPriceUsd', value)}
              size="small"
              fullWidth
              helperText="For non-AI usage or credit overage estimates"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={meterDraft.allowCreditOverage}
                  onChange={(event) => updateMeterDraft('allowCreditOverage', event.target.checked)}
                />
              }
              label="Allow credit overage"
            />
          </Stack>
          {meterDraft.kind === 'ai_tokens' && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <StyledTextField
                label="AI model key"
                value={meterDraft.aiModelKey}
                onChange={(value) => updateMeterDraft('aiModelKey', value)}
                size="small"
                fullWidth
                helperText="provider:model, for example openai:gpt-5.6-terra"
              />
              <StyledTextField
                label="Input USD per 1k"
                value={meterDraft.aiInputUsdPer1k}
                onChange={(value) => updateMeterDraft('aiInputUsdPer1k', value)}
                size="small"
                fullWidth
              />
              <StyledTextField
                label="Output USD per 1k"
                value={meterDraft.aiOutputUsdPer1k}
                onChange={(value) => updateMeterDraft('aiOutputUsdPer1k', value)}
                size="small"
                fullWidth
              />
            </Stack>
          )}
        </Stack>
      </SDialog>

      <Paper variant="outlined" sx={{ borderRadius: 1, mb: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_event, value: BillingTab) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          {BILLING_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          {activeTab === 'projects' && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6">Projects</Typography>
                <Typography variant="body2" color="text.secondary">
                  Search projects by display name, ID, owner, member email, or saved project aliases.
                </Typography>
              </Box>
              <TextField
                label="Search projects"
                value={projectSearch}
                onChange={(event) => setProjectSearch(event.target.value)}
                size="small"
                fullWidth
                helperText={projectSearchHelperText(projectSearch, projectsLoading, projects.length)}
              />
              {projectsLoading && projects.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={1}>
                  {projects.map((project) => (
                    <ProjectRow key={project.id} project={project} onOpen={openProjectDialog} />
                  ))}
                  {!projectsLoading && projects.length === 0 && (
                    <Alert severity="info">No projects found.</Alert>
                  )}
                </Stack>
              )}
            </Stack>
          )}

          {activeTab === 'meters' && (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6">Usage meters</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pricing rules for usage-based billing, including onboarding usage, SDK usage, and AI token models.
                  </Typography>
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateMeterDialog} disabled={saving}>
                  Add usage meter
                </Button>
              </Stack>
              <Stack spacing={1}>
                {meters.length === 0 ? (
                  <Alert severity="info">No usage meters configured yet.</Alert>
                ) : meters.map((meter) => (
                  <Paper key={meter.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2">{meter.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {meter.id} · {meter.kind} · {meter.unit}
                        </Typography>
                      </Box>
                      <Chip size="small" label={meter.status} />
                      {meter.unitAmountMinor !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          {moneyToLabel(meter.unitAmountMinor, meter.currency)} / {meter.unit}
                        </Typography>
                      )}
                      {meter.aiModelPrices && (
                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                          {Object.keys(meter.aiModelPrices).map((modelKey) => (
                            <Chip key={modelKey} size="small" label={modelKey} variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          )}

          {activeTab === 'types' && (
            loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                <Typography variant="h6">Subscription and credit products</Typography>
                {catalogProducts.map((product) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    saving={saving}
                    onEdit={openEditProductDialog}
                    onDelete={handleDeleteProductType}
                  />
                ))}
                {catalogProducts.length === 0 && (
                  <Alert severity="info">No platform subscription or credit products configured yet.</Alert>
                )}
              </Stack>
            )
          )}
        </Box>
      </Paper>

      <SDialog
        open={projectDialogOpen && Boolean(selectedProject)}
        onClose={closeProjectDialog}
        title="Project billing"
        maxWidth="md"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: 'Close',
          onClick: closeProjectDialog,
          disabled: saving,
        }}
      >
        {selectedProject && (
          <Stack spacing={1.5} sx={{ px: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6">{selectedProject.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedProject.id}
                {selectedProject.aliases?.length ? ` · also ${selectedProject.aliases.join(', ')}` : ''}
                {selectedProject.ownerId ? ` · owner ${selectedProject.ownerId}` : ''}
              </Typography>
              {selectedProject.memberEmails?.length ? (
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {selectedProject.memberEmails.slice(0, 8).map((email) => (
                    <Chip key={email} size="small" label={email} variant="outlined" />
                  ))}
                </Stack>
              ) : null}
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Project description</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedProject.description || 'No project description saved.'}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2">Project credit balance</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Internal Segmently credits available for AI overage billing.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  disabled={projectCreditsLoading || !selectedProjectId}
                  onClick={() => selectedProjectId && loadProjectCreditState(selectedProjectId)}
                >
                  Refresh credits
                </Button>
              </Stack>
              {projectCreditsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      icon={<AccountBalanceWalletIcon />}
                      label={`Available ${moneyToLabel(selectedAvailableCredits, selectedCreditCurrency)}`}
                    />
                    <Chip
                      variant="outlined"
                      label={`Reserved ${moneyToLabel(selectedReservedCredits, selectedCreditCurrency)}`}
                    />
                    <Chip
                      variant="outlined"
                      label={`Spendable ${moneyToLabel(selectedSpendableCredits, selectedCreditCurrency)}`}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Updated {projectAccount?.creditBalance.updatedAt
                      ? new Date(projectAccount.creditBalance.updatedAt).toLocaleString()
                      : 'not yet'}
                  </Typography>
                </Stack>
              )}
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">Manual project subscription</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add a finite or indefinite grant for this project.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={saving || !selectedProjectId}
                onClick={openCreateManualSubscriptionDialog}
                sx={{ minWidth: 230 }}
              >
                Add project subscription
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">Manual project credits</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add or reduce Segmently credits through immutable ledger entries for support, testing, or manual adjustments.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={saving || !selectedProjectId}
                  onClick={openCreditGrantDialog}
                  sx={{ minWidth: 210 }}
                >
                  Add project credits
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RemoveIcon />}
                  disabled={saving || !selectedProjectId || selectedSpendableCredits <= 0}
                  onClick={openCreditDebitDialog}
                  sx={{ minWidth: 210 }}
                >
                  Reduce credits
                </Button>
              </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>Credit ledger history</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  disabled={projectCreditsLoading}
                  onClick={() => setCreditHistoryDialogOpen(true)}
                >
                  View history
                </Button>
              </Stack>
              {projectCreditsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recentProjectCreditLedger.length > 0 ? (
                <Stack spacing={0.75}>
                  {recentProjectCreditLedger.map((entry) => (
                    <Paper key={entry.id} variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip size="small" label={entry.type} />
                            <Typography variant="subtitle2" color={entry.deltaMinor >= 0 ? 'success.main' : 'warning.main'}>
                              {ledgerDeltaLabel(entry)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Balance {moneyToLabel(entry.balanceAfterMinor, entry.currency)}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {entry.source}
                            {entry.sourceId ? ` · ${entry.sourceId}` : ''}
                            {entry.createdBy ? ` · by ${entry.createdBy}` : ''}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(entry.createdAt).toLocaleString()}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                  {projectCreditLedger.length > recentProjectCreditLedger.length && (
                    <Typography variant="caption" color="text.secondary">
                      Showing {recentProjectCreditLedger.length} of {projectCreditLedger.length} entries. Open history to filter by date and operation.
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Alert severity="info">No credit ledger entries yet.</Alert>
              )}
            </Paper>

            <Divider />

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                Project subscriptions
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                disabled={subscriptionsLoading || !selectedProjectId}
                onClick={() => selectedProjectId && loadProjectSubscriptions(selectedProjectId)}
              >
                Refresh
              </Button>
            </Stack>

            {subscriptionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack spacing={1}>
                {projectSubscriptions.map((subscription) => (
                  <SubscriptionRow
                    key={subscription.id}
                    subscription={subscription}
                    saving={saving}
                    onView={openViewSubscriptionDialog}
                    onEdit={openEditSubscriptionDialog}
                    onDelete={handleDeleteProjectSubscription}
                  />
                ))}
                {projectSubscriptions.length === 0 && (
                  <Alert severity="info">This project has no platform subscriptions yet.</Alert>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </SDialog>

      <SDialog
        open={manualSubscriptionDialogOpen && Boolean(selectedProject)}
        onClose={closeCreateManualSubscriptionDialog}
        title="Add project subscription"
        maxWidth="md"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeCreateManualSubscriptionDialog,
          disabled: saving,
        }}
        primaryAction={{
          label: 'Add project subscription',
          onClick: handleManualSubscription,
          loading: saving,
          disabled: !manualSubscriptionDraft.productId || !manualSubscriptionDraft.currentPeriodStart,
        }}
      >
        <Stack spacing={1.5} sx={{ px: 2 }}>
          <Alert severity="info">
            The subscription grants access from the start date until the end date. Leave the end date empty only for an indefinite manual grant.
          </Alert>
          {renderSubscriptionDraftFields(manualSubscriptionDraft, updateManualSubscriptionDraft)}
        </Stack>
      </SDialog>

      <SDialog
        open={creditGrantDialogOpen && Boolean(selectedProject)}
        onClose={closeCreditGrantDialog}
        title="Add project credits"
        maxWidth="sm"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeCreditGrantDialog,
          disabled: saving,
        }}
        primaryAction={{
          label: 'Add project credits',
          onClick: handleCreditGrant,
          loading: saving,
          disabled: !parseUsdToMinor(creditGrantDraft.amountUsd),
        }}
      >
        <Stack spacing={1.5} sx={{ px: 2 }}>
          <Alert severity="info">
            Credits are added to the internal Segmently balance and can be spent by AI overage billing. This does not grant subscription capabilities.
          </Alert>
          <TextField
            label="Amount USD"
            value={creditGrantDraft.amountUsd}
            onChange={(event) => updateCreditGrantDraft('amountUsd', event.target.value)}
            size="small"
            required
            fullWidth
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]+(\\.[0-9]{1,2})?' }}
            helperText="US dollars only. For example, 1.00 adds US$1.00."
          />
          <TextField
            label="Source"
            value={creditGrantDraft.source}
            onChange={(event) => updateCreditGrantDraft('source', event.target.value)}
            size="small"
            fullWidth
            helperText="Audit source; default is manual_admin_grant"
          />
          <TextField
            label="Source ID"
            value={creditGrantDraft.sourceId}
            onChange={(event) => updateCreditGrantDraft('sourceId', event.target.value)}
            size="small"
            fullWidth
            helperText="Generated automatically. Keep this key when retrying the same grant; change it for a new grant."
          />
        </Stack>
      </SDialog>

      <SDialog
        open={creditDebitDialogOpen && Boolean(selectedProject)}
        onClose={closeCreditDebitDialog}
        title="Reduce project credits"
        maxWidth="sm"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeCreditDebitDialog,
          disabled: saving,
        }}
        primaryAction={{
          label: 'Reduce credits',
          onClick: handleCreditDebit,
          loading: saving,
          disabled: !creditDebitAmountMinor || creditDebitAmountMinor > selectedSpendableCredits,
        }}
      >
        <Stack spacing={1.5} sx={{ px: 2 }}>
          <Alert severity="warning">
            This writes an immutable debit entry and lowers the internal Segmently credit balance. It does not edit or delete previous grants.
          </Alert>
          <Chip
            variant="outlined"
            label={`Spendable balance ${moneyToLabel(selectedSpendableCredits, selectedCreditCurrency)}`}
            sx={{ alignSelf: 'flex-start' }}
          />
          <TextField
            label="Amount USD"
            value={creditDebitDraft.amountUsd}
            onChange={(event) => updateCreditDebitDraft('amountUsd', event.target.value)}
            size="small"
            required
            fullWidth
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]+(\\.[0-9]{1,2})?' }}
            helperText="US dollars only. The amount cannot exceed spendable credits."
            error={Boolean(creditDebitAmountMinor && creditDebitAmountMinor > selectedSpendableCredits)}
          />
          <TextField
            label="Source"
            value={creditDebitDraft.source}
            onChange={(event) => updateCreditDebitDraft('source', event.target.value)}
            size="small"
            fullWidth
            helperText="Audit source; default is manual_admin_debit"
          />
          <TextField
            label="Source ID"
            value={creditDebitDraft.sourceId}
            onChange={(event) => updateCreditDebitDraft('sourceId', event.target.value)}
            size="small"
            fullWidth
            helperText="Generated automatically. Keep this key when retrying the same debit; change it for a new debit."
          />
        </Stack>
      </SDialog>

      <CreditLedgerHistoryDialog
        open={creditHistoryDialogOpen && Boolean(selectedProject)}
        onClose={() => setCreditHistoryDialogOpen(false)}
        ledger={projectCreditLedger}
        fallbackCurrency={selectedCreditCurrency}
        loading={projectCreditsLoading}
        onRefresh={() => (selectedProjectId ? loadProjectCreditState(selectedProjectId) : undefined)}
        onOpenBackendTask={(taskId) => setSelectedLedgerTaskId(taskId)}
      />

      <SDialog
        open={Boolean(subscriptionDialogMode && selectedSubscription)}
        onClose={closeSubscriptionDialog}
        title={subscriptionDialogMode === 'edit' ? 'Edit project subscription' : 'Project subscription'}
        maxWidth="sm"
        fullWidth
        showCloseButton={false}
        secondaryAction={{
          label: subscriptionDialogMode === 'edit' ? 'Cancel' : 'Close',
          onClick: closeSubscriptionDialog,
          disabled: saving,
        }}
        primaryAction={subscriptionDialogMode === 'edit' ? {
          label: 'Save subscription',
          onClick: handleSaveProjectSubscription,
          loading: saving,
          disabled: !subscriptionDraft?.productId || !subscriptionDraft?.currentPeriodStart,
        } : undefined}
      >
        {selectedSubscription && subscriptionDraft && (
          <Stack spacing={1.5} sx={{ px: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">{selectedSubscription.productName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedSubscription.id} · {selectedSubscription.productId}
              </Typography>
            </Stack>
            <Divider />
            {subscriptionDialogMode === 'view' ? (
              <Stack spacing={1}>
                <Typography variant="body2">Status: {selectedSubscription.status}</Typography>
                <Typography variant="body2">Funding source: {selectedSubscription.fundingSource}</Typography>
                <Typography variant="body2">
                  Period start: {new Date(selectedSubscription.currentPeriodStart).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Period end: {selectedSubscription.currentPeriodEnd
                    ? new Date(selectedSubscription.currentPeriodEnd).toLocaleString()
                    : 'No scheduled end'}
                </Typography>
                {selectedSubscription.trialEndsAt && (
                  <Typography variant="body2">
                    Trial ends: {new Date(selectedSubscription.trialEndsAt).toLocaleString()}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {selectedSubscription.capabilities.map((capability) => (
                    <Chip key={capability} size="small" label={capability} />
                  ))}
                </Stack>
              </Stack>
            ) : (
              renderSubscriptionDraftFields(subscriptionDraft, updateSubscriptionDraft)
            )}
          </Stack>
        )}
      </SDialog>

      <TaskDetailDialog
        open={Boolean(selectedLedgerTaskId)}
        taskId={selectedLedgerTaskId}
        onClose={() => setSelectedLedgerTaskId(null)}
      />

    </Box>
  );
}
